// AI Workforce Data Types and Constants

/**
 * @typedef {Object} AIService
 * @property {string} slug
 * @property {string} name
 * @property {string} shortName
 * @property {string} description
 * @property {string[]} outcomes
 * @property {string[]} deliverables
 * @property {string} monthlyFrom
 * @property {"Marketing" | "Sales" | "Operations" | "Compliance"} category
 */

/** @type {AIService[]} */
export const aiServices = [
  {
    slug: "ai-social-media-manager",
    name: "AI Social Media Manager",
    shortName: "Social Media",
    description:
      "Creates beverage-specific social calendars, launch campaigns, captions, content ideas, and post concepts for spirits, wine, RTD, sparkling, water, and functional brands.",
    outcomes: [
      "Consistent posting rhythm",
      "Launch campaign support",
      "Better retailer and distributor visibility",
    ],
    deliverables: [
      "Monthly content calendar",
      "Caption writing",
      "Campaign concepts",
      "Product launch support",
      "Promotional messaging",
    ],
    monthlyFrom: "$499",
    category: "Marketing",
  },
  {
    slug: "ai-executive-assistant",
    name: "AI Executive Assistant",
    shortName: "Assistant",
    description:
      "Helps beverage founders stay organized with reminders, follow-up lists, meeting prep, CRM notes, task summaries, and structured admin support.",
    outcomes: [
      "Cleaner follow-up process",
      "Less missed opportunity",
      "Better founder organization",
    ],
    deliverables: [
      "Follow-up reminders",
      "Task organization",
      "Meeting prep notes",
      "Weekly action summaries",
      "CRM-ready notes",
    ],
    monthlyFrom: "$499",
    category: "Operations",
  },
  {
    slug: "ai-blogger-seo-writer",
    name: "AI Blogger / SEO Writer",
    shortName: "Blogger",
    description:
      "Writes SEO content, blog posts, product page copy, launch articles, founder stories, and educational beverage content designed to help brands get discovered.",
    outcomes: [
      "More searchable content",
      "Stronger website authority",
      "Better storytelling",
    ],
    deliverables: [
      "SEO blog posts",
      "Product page copy",
      "Brand stories",
      "Educational articles",
      "Keyword-driven content planning",
    ],
    monthlyFrom: "$599",
    category: "Marketing",
  },
  {
    slug: "ai-sales-scout",
    name: "AI Sales Scout",
    shortName: "Sales Scout",
    description:
      "Identifies beverage buyers, distributors, retailers, on-premise accounts, and strategic targets based on geography, category, and positioning.",
    outcomes: [
      "Faster prospecting",
      "Cleaner buyer lists",
      "Improved account targeting",
    ],
    deliverables: [
      "Lead lists",
      "Account research",
      "Buyer targeting",
      "Distributor targeting",
      "Prospect summaries",
    ],
    monthlyFrom: "$799",
    category: "Sales",
  },
  {
    slug: "ai-outreach-rep",
    name: "AI Outreach Rep",
    shortName: "Outreach Rep",
    description:
      "Drafts structured outreach, follow-ups, intro emails, tasting requests, distributor pitches, and account re-engagement messages for beverage operators.",
    outcomes: [
      "More consistent outreach",
      "Faster follow-up",
      "Cleaner sales messaging",
    ],
    deliverables: [
      "Intro email drafts",
      "Follow-up sequences",
      "Retail outreach messaging",
      "Distributor pitch support",
      "Sampling/tasting outreach",
    ],
    monthlyFrom: "$999",
    category: "Sales",
  },
  {
    slug: "ai-compliance-assistant",
    name: "AI Compliance Assistant",
    shortName: "Compliance",
    description:
      "Helps organize beverage documentation, product specs, submission inputs, registration checklists, label-review prep, and compliance workflows. It is not a law firm and does not provide legal representation.",
    outcomes: [
      "Better document organization",
      "Cleaner submission prep",
      "Less compliance chaos",
    ],
    deliverables: [
      "Checklist generation",
      "Label-review prep",
      "Submission input organization",
      "State/federal tracking sheets",
      "Documentation summaries",
    ],
    monthlyFrom: "$999",
    category: "Compliance",
  },
];

export const aiPackages = [
  {
    name: "Starter AI",
    price: "$499/mo",
    description: "One focused AI function for a beverage brand that needs targeted support.",
    includes: [
      "1 AI service",
      "Monthly planning call structure",
      "Basic reporting",
      "Email support",
    ],
  },
  {
    name: "Growth AI",
    price: "$1,500/mo",
    description: "A bundled AI stack for brands that need marketing, sales, and admin support.",
    includes: [
      "Up to 3 AI services",
      "Monthly workflow review",
      "Priority delivery",
      "Structured deliverable cadence",
    ],
    featured: true,
  },
  {
    name: "Launch AI",
    price: "$3,500/mo",
    description: "A managed AI support layer for beverage brands preparing to scale or launch.",
    includes: [
      "Full multi-service support",
      "Custom workflow design",
      "Launch support structure",
      "Priority strategy access",
    ],
  },
];

// Workforce cards for homepage display
export const workforceCards = [
  {
    title: "AI Social Media Manager",
    text: "Campaign ideas, captions, launch calendars, and content support for beverage brands.",
    icon: "Megaphone",
  },
  {
    title: "AI Executive Assistant",
    text: "Task tracking, meeting prep, follow-up reminders, and founder support workflows.",
    icon: "UserRound",
  },
  {
    title: "AI Blogger / SEO Writer",
    text: "SEO blogs, product copy, brand stories, and educational beverage content.",
    icon: "FileText",
  },
  {
    title: "AI Sales Scout",
    text: "Buyer lists, distributor targeting, account research, and prospecting support.",
    icon: "Target",
  },
  {
    title: "AI Outreach Rep",
    text: "Intro emails, tasting requests, follow-ups, and distributor pitch support.",
    icon: "Brain",
  },
  {
    title: "AI Compliance Assistant",
    text: "Document organization, submission prep, label-review workflow, and tracking support.",
    icon: "ShieldCheck",
  },
];

// Primary need options for contact form
export const primaryNeedOptions = [
  "Build a beverage brand",
  "Supplier introductions",
  "Bottle or can sourcing",
  "Label sourcing",
  "AI Social Media Manager",
  "AI Executive Assistant",
  "AI Blogger / SEO Writer",
  "AI Sales Scout",
  "AI Outreach Rep",
  "AI Compliance Assistant",
  "Custom beverage AI package",
];
